import React, { useEffect, useState } from 'react';
import { Tabs, message, Spin } from 'antd';
import { useAuth } from '../../contexts/AppContext';
import FloatingFooter from '../../components/utils/FloatingFooter';
import BasicInfoTab from './BasicInfoTab';
import MediaTab from './MediaTab';
import { create } from "zustand";

export const useVenueStore = create((set) => ({
  validity: null,               // 你要共享的字段
  setValidity: (validity) => set({ validity: validity }),
}));



const TsVenueLanding = () => {
  // 从上下文中获取当前 token 和选中的 venueId
  const { token, selectedRole } = useAuth();
  const venueId = selectedRole?.id || '';
  const apiBase = process.env.REACT_APP_API_BASE_URL;


  // 页面状态管理
  const [formData, setFormData] = useState(null);         // 场馆表单数据
  const [loading, setLoading] = useState(false);          // 是否加载中
  const [, setSaving] = useState(false);            // 是否正在保存
  const [hasChanges, setHasChanges] = useState(false);    // 是否有修改

  // 修改顶层字段，如 name、alias 等
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // 嵌套字段修改，如 landing.venueSlides、theme.background 等
  const handleNested = (group, key, value) => {
    setFormData(prev => ({
      ...prev,
      [group]: { ...prev[group], [key]: value },
    }));
    setHasChanges(true);
  };

  // 兼容性处理函数：支持将字符串形式的数组（如 '{"a","b"}'）转为真数组
  const parseSlideInput = (input) => {
    if (Array.isArray(input)) return input;
    if (typeof input !== 'string') return [];

    // 特殊格式：{"a","b"}
    if (input.startsWith('{') && input.endsWith('}')) {
      return input
        .slice(1, -1)                     // 去除花括号
        .split(',')                       // 按逗号分割
        .map(s => s.trim().replace(/^"|"$/g, '')) // 去除每项的引号
        .filter(Boolean);                // 过滤空字符串
    }

    // 常规逗号字符串：'a,b,c'
    return input.split(',').map(s => s.trim()).filter(Boolean);
  };

  // 拉取场馆初始数据
  const fetchVenueData = async () => {
    if (!token || !venueId) {
      message.error('Missing token or venueId');
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(`${apiBase}/ts/${venueId}/basic-info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!resp.ok) {
        message.error('Failed to fetch venue data.');
        return;
      }

      const data = await resp.json();
      useVenueStore.getState().setValidity(data.validity.statusLogs[0]); //准备传给sidebar

      setFormData({
        id: data.id,
        name: data.name || '',
        alias: data.alias || '',
        permissionLevel: data.permissionLevel ?? 0,
        theme: data.theme || {},
        landing: {
          venueLogo: data.landing?.venueLogo || '',
          venueSlides: data.landing?.venueSlides || [], // 保持数组
          venueVideos: (data.landing?.venueVideos || []),
          liveInfo: Array.isArray(data.landing?.liveInfo)
            ? (data.landing.liveInfo[0] || 'flight')
            : (data.landing?.liveInfo || 'flight'),
          bottomSlides: data.landing?.bottomSlides || [], // 保持数组
          welcomeBanner: data.landing?.welcomeBanner || '',
          geoLocation: data.landing?.geoLocation || { latitude: -9.45, longitude: 147.18 },
        },
        landingBanner: data.landingBanner || '',
      });

      setHasChanges(false);
    } catch (err) {
      console.error(err);
      message.error('Network error while fetching venue data.');
    } finally {
      setLoading(false);
    }
  };

  // 保存提交函数（调用唯一接口）
  const handleSaveAll = async () => {
    if (!token || !venueId || !formData) {
      message.error('Missing required data');
      return;
    }

    setSaving(true);
    try {
      // 去掉 Showcase 第一张图（由后端自动生成）
      const allSlides = parseSlideInput(formData.landing.venueSlides);

      const hasLandingBanner =
        !!formData.landingBanner &&
        formData.landingBanner.trim() !== '';

      console.log('hasLandingBanner:', hasLandingBanner);
      console.log('allSlides:', allSlides);

      const savedSlides = Array.isArray(allSlides)
        ? hasLandingBanner
          ? allSlides.slice(1)
          : allSlides // 
        : [];

      const payload = {
        name: formData.name,
        alias: formData.alias,
        permissionLevel: Number(formData.permissionLevel),
        theme: { ...formData.theme },
        landing: {
          venueLogo: formData.landing.venueLogo,
          venueSlides: savedSlides,
          bottomSlides: parseSlideInput(formData.landing.bottomSlides),
          welcomeBanner: formData.landing.welcomeBanner,
          venueVideos: parseSlideInput(formData.landing.venueVideos),
          liveInfo: formData.landing.liveInfo,
          geoLocation: formData.landing.geoLocation,
        },
      };

      const resp = await fetch(`${apiBase}/ts/tsvenue/${venueId}/update-venue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        message.error('Failed to save venue.');
        return;
      }

      message.success('Changes saved successfully.');
      setHasChanges(false);
    } catch (err) {
      console.error(err);
      message.error('Network error while saving.');
    } finally {
      setSaving(false);
    }
  };

  // 页面初始化时加载数据
  useEffect(() => {
    fetchVenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  return (
    <div className="min-h-screen bg-gray-100 pb-[8rem] overflow-hidden">
      <div className="max-w-7xl w-[92%] mx-auto pt-3 overflow-hidden">
        <div className="bg-white shadow-lg rounded-2xl p-6">
          {!venueId ? (
            <div className="text-center text-red-500 py-12">No venueId found.</div>
          ) : loading || !formData ? (
            <div className="flex justify-center items-center py-16">
              <Spin size="large" />
            </div>
          ) : (
            <Tabs defaultActiveKey="general" type="card" className="rounded-xl bg-white">
              <Tabs.TabPane tab="General Settings" key="general">
                <BasicInfoTab
                  formData={formData}
                  onChange={handleChange}
                  onNested={handleNested}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Media Info" key="media">
                <MediaTab
                  formData={formData}
                  onNested={handleNested}
                />
              </Tabs.TabPane>
            </Tabs>
          )}
        </div>
      </div>

      {/* 页面底部悬浮保存栏 */}
      {formData && (
        <FloatingFooter
          hasChanges={hasChanges}
          onSave={handleSaveAll}
          saveLabel="Save All"
          multipleSave={false}
        />
      )}
    </div>
  );
};

export default TsVenueLanding;

